<template>
  <div>
    <vs-alert
      color="danger"
      title="User Not Found"
      :active.sync="user_not_found"
    >
      No Records Found!!!
    </vs-alert>
    <div>
      <vx-card
        class="scroll-content"
        title="Hours Rendered"
        subtitle="Select an employee, then manage their payroll adjustments"
      >
        <div class="hours-rendered-step-heading">
          <span class="hours-rendered-step-number">1</span>
          <div>
            <h4>Select an employee</h4>
            <p>Click a row below to choose whose records you want to manage.</p>
          </div>
        </div>
        <vs-progress
          v-if="isLoadingEmployees"
          indeterminate
          color="primary"
          class="mb-4"
        />
        <div
          v-if="hoursRenderedDataLoading && !hasSelectedEmployee"
          class="hours-rendered-inline-loading mb-4"
        >
          Payroll records are being prepared in the background. You can select
          an employee while this finishes.
        </div>
        <div v-if="employeeLoadError" class="hours-rendered-tab-error mb-4">
          <span>The employee list could not be loaded.</span>
          <vs-button type="border" size="small" @click="loadEmployees">
            Try again
          </vs-button>
        </div>
        <!-- Information - Col 1 -->
        <div class="vx-row">
          <div class="vx-col w-full mb-base">
            <template>
              <vs-table
                v-if="!isLoadingEmployees && !employeeLoadError"
                stripe
                v-model="selected"
                max-items="8"
                pagination
                search
                :data="getAllEmployees"
              >
                <template slot="thead">
                  <vs-th width="20px"></vs-th>
                  <vs-th>Code</vs-th>
                  <vs-th>Last Name</vs-th>
                  <vs-th>First Name</vs-th>
                  <vs-th>Middle Name</vs-th>
                  <vs-th>Status</vs-th>
                  <vs-th>Department</vs-th>
                </template>
                <template slot-scope="{ data }">
                  <vs-tr
                    :data="tr"
                    :key="indextr"
                    v-for="(tr, indextr) in data"
                  >
                    <vs-td :data="data[indextr].photoUrl">
                      <div v-if="data[indextr].photoUrl != ''">
                        <vs-avatar
                          :src="data[indextr].photoUrl"
                          class="flex-shrink-0 mr-2"
                          size="30px"
                        />
                      </div>
                      <div v-else>
                        <vs-avatar
                          class="rounded-full shadow-md cursor-pointer block"
                          size="40"
                          :text="data[indextr].fullName"
                          :color="
                            data[indextr].fullName.length < 12
                              ? 'success'
                              : data[indextr].fullName.length <= 15
                              ? 'primary'
                              : data[indextr].fullName.length <= 20
                              ? 'warning'
                              : 'danger'
                          "
                        />
                      </div>
                    </vs-td>
                    <vs-td :data="data[indextr].employeeCode">
                      {{ data[indextr].employeeCode }}
                    </vs-td>
                    <vs-td :data="data[indextr].lastName">
                      {{ data[indextr].lastName }}
                    </vs-td>
                    <vs-td :data="data[indextr].firstName">
                      {{ data[indextr].firstName }}
                    </vs-td>
                    <vs-td :data="data[indextr].middleName">
                      {{ data[indextr].middleName }}
                    </vs-td>
                    <vs-td>
                      {{ employeeStatusLabel(data[indextr]) }}
                    </vs-td>
                    <vs-td
                      :data="
                        data[indextr].employeeIdentification
                          ? data[indextr].employeeIdentification.departmentId
                          : null
                      "
                    >
                      {{ departmentLabel(data[indextr]) }}
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
            </template>
          </div>
        </div>
        <div
          v-if="hasSelectedEmployee"
          class="hours-rendered-selected-employee"
        >
          <div class="hours-rendered-selected-main">
            <vs-avatar
              v-if="selected.photoUrl"
              :src="selected.photoUrl"
              size="48px"
            />
            <vs-avatar
              v-else
              :text="selectedEmployeeName"
              color="primary"
              size="48px"
            />
            <div>
              <div class="hours-rendered-selected-label">
                <feather-icon icon="CheckCircleIcon" svgClasses="h-4 w-4" />
                Selected employee
              </div>
              <h3>{{ selectedEmployeeName }}</h3>
              <p>
                {{ selected.employeeCode || "No employee code" }}
                <span class="hours-rendered-divider">•</span>
                {{ departmentLabel(selected) }}
              </p>
            </div>
          </div>
          <vs-button
            type="border"
            color="primary"
            size="small"
            icon-pack="feather"
            icon="icon-x"
            @click="clearSelection"
          >
            Clear selection
          </vs-button>
        </div>
        <div v-else class="hours-rendered-empty-selection">
          <feather-icon icon="UserCheckIcon" svgClasses="h-8 w-8" />
          <div>
            <h4>No employee selected</h4>
            <p>Select a row above to unlock Overtime, Earnings, Deductions, and Loans.</p>
          </div>
        </div>
        <div v-if="hasSelectedEmployee" class="hours-rendered-step-heading mt-8">
          <span class="hours-rendered-step-number">2</span>
          <div>
            <h4>Manage payroll records</h4>
            <p>The tabs below now apply to {{ selectedEmployeeName }}.</p>
          </div>
        </div>
        <div
          v-if="hasSelectedEmployee && hoursRenderedDataLoading"
          class="hours-rendered-inline-loading mb-4"
        >
          <vs-progress indeterminate color="primary" class="mb-2" />
          Loading the cached payroll records for the first time…
        </div>
        <div
          v-else-if="hasSelectedEmployee && hoursRenderedDataError"
          class="hours-rendered-tab-error mb-4"
        >
          <span>{{ hoursRenderedDataError }}</span>
          <vs-button type="border" size="small" @click="preloadPayrollData(true)">
            Try again
          </vs-button>
        </div>
        <div
          v-if="hasSelectedEmployee && hoursRenderedDataLoaded"
          class="vx-row"
        >
          <div class="vx-col w-full mb-base">
            <vs-tabs
              :key="userId"
              v-model="activeTab"
              class="tab-action-btn-fill-container"
            >
              <vs-tab label="Overtime" icon-pack="feather" icon="icon-clock">
                <div class="tab-text">
                  <overtime :paymentMode="paymentModeId" :userId="userId" />
                </div>
              </vs-tab>
              <vs-tab label="Earnings" icon-pack="feather" icon="icon-plus">
                <div class="tab-text">
                  <earnings :paymentMode="paymentModeId" :userId="userId" />
                </div>
              </vs-tab>
              <vs-tab label="Deductions" icon-pack="feather" icon="icon-minus">
                <div class="tab-text">
                  <deductions :paymentMode="paymentModeId" :userId="userId" />
                </div>
              </vs-tab>
              <vs-tab label="Loans" icon-pack="feather" icon="icon-share-2">
                <div class="tab-text">
                  <loans :paymentMode="paymentModeId" :userId="userId" />
                </div>
              </vs-tab>
            </vs-tabs>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import Overtime from "./Overtime.vue";
import Earnings from "./Earnings.vue";
import Deductions from "./Deductions.vue";
import Loans from "./Loans.vue";
export default {
  components: {
    Overtime,
    Earnings,
    Deductions,
    Loans,
  },

  data() {
    return {
      user_data: null,
      user_not_found: false,

      selected: [],
      activeTab: 0,
      isLoadingEmployees: true,
      employeeLoadError: false,

      paymentModeId: 0,
      userId: "",
    };
  },
  created() {
    this.loadEmployees();
    this.preloadPayrollData();
    if (!this.$store.state.lookup.getAllDepartment.length) {
      this.$store.dispatch("lookup/getAllDepartment").catch(() => {});
    }
  },
  watch: {
    selected(employee) {
      if (!employee || !employee.id) {
        this.paymentModeId = 0;
        this.userId = "";
        this.activeTab = 0;
        return;
      }

      const salaryInformation = employee.employeeSalaryInformation || {};
      this.paymentModeId = salaryInformation.paymentModeId || 0;
      this.userId = employee.id;
      this.activeTab = 0;
    },
  },

  computed: {
    getAllEmployees() {
      return this.$store.getters["hrms/getAllRealEmployees"]() || [];
    },
    hasSelectedEmployee() {
      return Boolean(this.selected && this.selected.id);
    },
    hoursRenderedDataLoaded() {
      return this.$store.state.payroll.hoursRenderedDataLoaded;
    },
    hoursRenderedDataLoading() {
      return this.$store.state.payroll.hoursRenderedDataLoading;
    },
    hoursRenderedDataError() {
      return this.$store.state.payroll.hoursRenderedDataError;
    },
    selectedEmployeeName() {
      if (!this.hasSelectedEmployee) return "";
      return (
        this.selected.fullName ||
        [this.selected.firstName, this.selected.middleName, this.selected.lastName]
          .filter(Boolean)
          .join(" ") ||
        "Unnamed employee"
      );
    },
    empStatusName() {
      return (id) => this.$store.getters["lookup/empStatus"](id);
    },
    departmentName() {
      return (id) => this.$store.getters["lookup/department"](id);
    },
  },
  methods: {
    preloadPayrollData(force) {
      return this.$store
        .dispatch("payroll/preloadHoursRenderedData", { force: Boolean(force) })
        .catch(() => {});
    },
    loadEmployees(force) {
      if (!force && this.$store.state.hrms.allEmployee.length) {
        this.isLoadingEmployees = false;
        return Promise.resolve();
      }
      this.isLoadingEmployees = true;
      this.employeeLoadError = false;
      this.user_not_found = false;
      return this.$store
        .dispatch("hrms/getAllEmployee", {
          pageNumber: 1,
          pageSize: -1,
        })
        .then(() => {
          this.isLoadingEmployees = false;
        })
        .catch((error) => {
          this.isLoadingEmployees = false;
          if (error.response && error.response.status === 404) {
            this.user_not_found = true;
            return;
          }
          this.employeeLoadError = true;
        });
    },
    clearSelection() {
      this.selected = [];
    },
    employeeStatusLabel(employee) {
      const information = employee && employee.employeeInformation;
      if (!information || information.employeeStatus == null) return "None";
      const status = this.empStatusName(information.employeeStatus);
      return status ? status.status : "Loading…";
    },
    departmentLabel(employee) {
      const identification = employee && employee.employeeIdentification;
      if (!identification || identification.departmentId == null) {
        return "No department";
      }
      const department = this.departmentName(identification.departmentId);
      return department ? department.departmentName : "Loading…";
    },
    //  fetch_user_data() {
    //      this.user_data = this.$store.getters['hrms/getAllRealEmployees']()
    //      console.log('USER_DATA')
    //      console.log(this.user_data)
    //  }
    // getEarnings(userId) {
    //     this.employeeEarnings = this.$store.getters['payroll/getEarningsByUserId'](userId)
    //     //console.log(this.employeeEarnings)
    // },
  },
};
</script>
<style lang="scss">
#avatar-col {
  width: 10rem;
}

.hours-rendered-step-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;

  h4 {
    margin: 0 0 0.2rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #6b7280;
  }
}

.hours-rendered-step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 2rem;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: rgba(var(--vs-primary), 0.12);
  color: rgb(var(--vs-primary));
  font-weight: 700;
}

.hours-rendered-selected-employee,
.hours-rendered-empty-selection,
.hours-rendered-tab-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 0.65rem;
}

.hours-rendered-selected-employee {
  border: 1px solid rgba(var(--vs-success), 0.35);
  background: rgba(var(--vs-success), 0.07);
}

.hours-rendered-selected-main,
.hours-rendered-empty-selection {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.hours-rendered-selected-main h3,
.hours-rendered-empty-selection h4 {
  margin: 0.15rem 0;
}

.hours-rendered-selected-main p,
.hours-rendered-empty-selection p {
  margin: 0;
  color: #6b7280;
}

.hours-rendered-selected-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: rgb(var(--vs-success));
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.035em;
}

.hours-rendered-divider {
  padding: 0 0.35rem;
}

.hours-rendered-empty-selection {
  justify-content: flex-start;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  color: #64748b;
}

.hours-rendered-tab-error {
  border: 1px solid rgba(var(--vs-danger), 0.3);
  background: rgba(var(--vs-danger), 0.06);
  color: rgb(var(--vs-danger));
}

.hours-rendered-inline-loading {
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: rgba(var(--vs-primary), 0.07);
  color: rgb(var(--vs-primary));
}

.hours-rendered-record-switch {
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.65rem;
  background: #f8fafc;

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.55rem 0.8rem;
    border: 0;
    border-radius: 0.45rem;
    background: transparent;
    color: #64748b;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;

    span {
      min-width: 1.4rem;
      padding: 0.08rem 0.35rem;
      border-radius: 9999px;
      background: #e2e8f0;
      color: #475569;
      font-size: 0.75rem;
      text-align: center;
    }

    &.active {
      background: #fff;
      box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
      color: rgb(var(--vs-primary));

      span {
        background: rgba(var(--vs-primary), 0.12);
        color: rgb(var(--vs-primary));
      }
    }
  }
}

.hours-rendered-record-empty {
  padding: 2.5rem 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 0.65rem;
  background: #f8fafc;
  color: #64748b;
  text-align: center;

  h4 {
    margin: 0.75rem 0 0.25rem;
    color: #334155;
  }

  p {
    margin: 0;
  }
}

@media screen and (max-width: 640px) {
  .hours-rendered-selected-employee,
  .hours-rendered-tab-error {
    align-items: flex-start;
    flex-direction: column;
  }

  .hours-rendered-record-switch {
    display: flex;

    button {
      flex: 1;
      justify-content: center;
    }
  }
}

#page-user-view {
  table {
    td {
      vertical-align: top;
      min-width: 10px;
      padding-bottom: 0.8rem;
      word-break: break-all;
    }

    &:not(.permissions-table) {
      td {
        @media screen and (max-width: 370px) {
          display: block;
        }
      }
    }
  }
}

@media screen and (min-width: 1201px) and (max-width: 1211px),
  only screen and (min-width: 636px) and (max-width: 991px) {
  #account-info-col-1 {
    width: calc(100% - 12rem) !important;
  }
}
</style>
